/**
 * Is this the installed app, or a browser tab? Account creation asks (see services/api/src/market/install.ts), because a
 * passkey lives on the device and an account made in a tab that is then closed is the one people lose.
 *
 * Three ways of being installed, all reported to the server in one header: a store app (Capacitor), a home-screen web app
 * (display-mode standalone, or iOS's own flag), and the Android store app that wraps the site (its first load has an
 * android-app:// referrer). A browser tab is none of them. This is a product rule, not security: it is what the client says.
 */
export type InstallMode = 'native' | 'standalone' | 'twa'
export type Platform = 'ios' | 'android' | 'desktop'

type Nav = Navigator & { standalone?: boolean; maxTouchPoints: number }
type Win = Window & { Capacitor?: { isNativePlatform?: () => boolean } }

export function installMode(): InstallMode | null {
  if (typeof window === 'undefined') return null
  if ((window as Win).Capacitor?.isNativePlatform?.() === true) return 'native'
  const standalone = ['standalone', 'fullscreen'].some(m => window.matchMedia?.(`(display-mode: ${m})`).matches) || (navigator as Nav).standalone === true
  if (standalone) return 'standalone'
  if (document.referrer.startsWith('android-app://')) return 'twa'
  return null
}

export function platform(): Platform {
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && (navigator as Nav).maxTouchPoints > 1)) return 'ios'
  if (/Android/.test(ua)) return 'android'
  return 'desktop'
}

/** Facebook, Instagram and similar open links in their own browser, which cannot install anything. */
export function inAppBrowser(): boolean {
  const ua = navigator.userAgent
  return /FBAN|FBAV|FB_IAB|Instagram|Line\/|Twitter|TikTok|Snapchat|MicroMessenger/i.test(ua) || (/iPhone|iPad|iPod/.test(ua) && /AppleWebKit/.test(ua) && !/Safari/.test(ua))
}

// Chrome offers to install from a button, but only if the page catches the event early and keeps it.
type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }
let deferred: InstallEvent | null = null
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', e => { e.preventDefault(); deferred = e as InstallEvent })
}
export const canPromptInstall = () => deferred !== null
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false
  const asked = deferred
  deferred = null
  await asked.prompt()
  return (await asked.userChoice).outcome === 'accepted'
}
